import { isDev, Log, useProxyWatch, useProxyWatchUpdates, useTranslation } from '@fanfanlo';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IMatrixMessage } from '../../client';
import { matrixLoadHistory } from '../../client/message/handlers/loadHistory';
import { matrixRoom } from '../../client/room';
import { matrixUser } from '../../client/user';
import { MessageItem } from './MessageItem';

interface MessageListVirtuosoProps {
  roomId: string;
}

const fileLogger = new Log(false, 'MessageListVirtuoso');
// fileLogger.pause = true;
// fileLogger.childrenPaused = true;

export function MessageListVirtuoso({ roomId }: MessageListVirtuosoProps) {
  const logger = fileLogger.sub(false,'MessageListVirtuoso_ui');
  logger.pause = true;
  // logger.childrenPaused = true;

  const { t } = useTranslation('homepage/im/components/room/content');

  // 监听登录状态
  const [loginResponse] = useProxyWatch(
    matrixUser.userData,
    'userInfo.loginResponse',
    matrixUser.userData.userInfo.loginResponse
  );

  // 监听消息列表
  const [messageIds] = useProxyWatchUpdates(
    matrixRoom.roomData.messages,
    [`${roomId}`],
    matrixRoom.roomData.messages[roomId] || []
  );
  const messagesMap = matrixRoom.roomData.messagesMap;

  // 获取消息详情并转换为 IMatrixMessage 格式
  const messageList = (messageIds?.map(id => {
    const message = messagesMap[id];
    if (!message) return null;
    
    // 记录紧急联系人邀请消息
    if (message.content?.custom?.['app.emergency_contact.invite']) {
      logger.log('发现紧急联系人邀请消息:', {
        messageId: id,
        content: message.content,
        type: message.type,
        timestamp: new Date(message.origin_server_ts).toISOString()
      });
    }
    
    return {
      event_id: message.event_id,
      type: message.type || message.content.msgtype, // 使用消息的 type 属性，如果不存在则使用 content.msgtype
      sender: message.sender,
      origin_server_ts: message.origin_server_ts,
      room_id: roomId || '',
      content: message.content,
    };
  }).filter((item): item is NonNullable<typeof item> => item !== null) || []) as IMatrixMessage[];
  
  // 记录处理后的消息列表
  logger.log('处理后的消息列表:', {
    roomId,
    messageCount: messageList.length,
    messageTypes: [...new Set(messageList.map(msg => msg.type))],
    emergencyInvites: messageList.filter(msg => 
      msg.content?.custom?.['app.emergency_contact.invite']
    ).length
  });

  const [loading, setLoading] = useState(false);
  const [historyEnd, setHistoryEnd] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const virtuosoRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // 补偿相关
  const anchorMsgIdRef = useRef<string | null>(null); // 记录锚点消息id
  const [autoCompensate, setAutoCompensate] = useState(false);

  // 上拉加载更多历史消息，加载前记录 anchorMsgIdRef
  const onLoadMore = useCallback(async () => {
    logger.log('onLoadMore called', {
      roomId,
      messageListLen: messageList.length,
      loading,
      historyEnd,
    });
    if (!roomId || messageList.length === 0 || loading || historyEnd) return;
    setLoading(true);
    setLoadingHistory(true);
    try {
      anchorMsgIdRef.current = messageList[0]?.event_id;
      const res = await matrixLoadHistory.loadHistoryByEventId(roomId, messageList[0].event_id, isDev ? 30 : 10);
      logger.log('res=', res);
      if (res.isEnd) setHistoryEnd(true);
      setAutoCompensate(true); // 加载后触发补偿
    } finally {
      setLoading(false);
      setLoadingHistory(false);
    }
  }, [roomId, messageList, loading, historyEnd]);

  // 在 totalListHeightChanged 里做自动补偿
  const handleTotalListHeightChanged = useCallback((height: number) => {
    logger.log('[totalListHeightChanged] compensation check', { height, autoCompensate, anchorMsg: anchorMsgIdRef.current });
    if (autoCompensate && anchorMsgIdRef.current) {
      const newIndex = messageList.findIndex(msg => msg.event_id === anchorMsgIdRef.current);
      logger.log('[autoCompensate] anchorMsgId', anchorMsgIdRef.current, 'newIndex', newIndex);
      if (newIndex !== -1) {
        setTimeout(() => {
          logger.log('[autoScroll] scrollToIndex', { newIndex, anchorMsgId: anchorMsgIdRef.current });
          virtuosoRef.current?.scrollToIndex({ index: newIndex, align: 'start', 
            // behavior: 'smooth' 
          });
        }, 30);
      }
      setAutoCompensate(false);
    }
  }, [autoCompensate, messageList]);

  // 监听用户主动操作，停止自动补偿
  useEffect(() => {
    const stopCompensate = () => {
      logger.log('[userAction] stop autoCompensate');
      setAutoCompensate(false);
    };
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('mousedown', stopCompensate);
    el.addEventListener('touchstart', stopCompensate);
    return () => {
      el.removeEventListener('mousedown', stopCompensate);
      el.removeEventListener('touchstart', stopCompensate);
    };
  }, []);

  // 每次到顶都尝试加载历史
  const handleAtTopStateChange = useCallback((atTop: boolean) => {
    logger.log('[atTopStateChange]', { atTop, historyEnd, loading, loadingHistory });
    if (atTop && !historyEnd && !loading && !loadingHistory) {
      onLoadMore();
    }
  }, [historyEnd, loading, loadingHistory, onLoadMore]);

  logger.log('messageList.length=', messageList.length);
  return (
    // <div ref={containerRef} style={{ height: '100%', width: '100%' }}>
      <Virtuoso
        ref={virtuosoRef}
        totalListHeightChanged={handleTotalListHeightChanged}
        style={{ height: '100%', width: '100%' }}
        data={messageList}
        itemContent={(index, message) => (
          <MessageItem
            message={message}
            currentUserId={loginResponse?.user_id || ''}
          />
        )}
        followOutput={"auto"}
        atTopStateChange={handleAtTopStateChange}
        components={{
          EmptyPlaceholder: () => (
            <div style={{ textAlign: 'center', color: '#888', padding: 16 }}>
              {t('MessageList.noMessages')}
            </div>
          ),
          Header: () =>
            loading ? (
              <div style={{ textAlign: 'center', color: '#888', padding: 8 }}>
                {t('MessageList.loadingHistory')}
              </div>
            ) : historyEnd ? (
              <div style={{ textAlign: 'center', color: '#888', padding: 8 }}>
                {t('MessageList.historyEnd')}
              </div>
            ) : null,
        }}
      />
    // </div>
  );
}

